import React, { useState, useEffect, useCallback } from 'react';
import { TimeInput } from '@/components/ui/date-input';
import { Clock, Plus, Edit, Trash2, Loader2, Save, Star, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' }
];

const ShiftsManagement = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    start_time: '09:00',
    end_time: '18:00',
    grace_period_minutes: 10,
    late_threshold_minutes: 60,
    half_day_threshold_minutes: 240,
    working_days: [0, 1, 2, 3, 4, 5],
    is_default: false
  });

  const fetchShifts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/shifts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShifts(response.data.shifts || []);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const handleOpenDialog = (shift = null) => {
    if (shift) {
      setEditingShift(shift);
      setForm({
        name: shift.name || '',
        start_time: shift.start_time || '09:00',
        end_time: shift.end_time || '18:00',
        grace_period_minutes: shift.grace_period_minutes || 10,
        late_threshold_minutes: shift.late_threshold_minutes || 60,
        half_day_threshold_minutes: shift.half_day_threshold_minutes || 240,
        working_days: shift.working_days || [0, 1, 2, 3, 4, 5],
        is_default: shift.is_default || false
      });
    } else {
      setEditingShift(null);
      setForm({
        name: '',
        start_time: '09:00',
        end_time: '18:00',
        grace_period_minutes: 10,
        late_threshold_minutes: 60,
        half_day_threshold_minutes: 240,
        working_days: [0, 1, 2, 3, 4, 5],
        is_default: false
      });
    }
    setDialogOpen(true);
  };

  const toggleDay = (dayValue) => {
    setForm(prev => {
      const days = prev.working_days.includes(dayValue)
        ? prev.working_days.filter(d => d !== dayValue)
        : [...prev.working_days, dayValue].sort();
      return { ...prev, working_days: days };
    });
  };

  const handleSave = async () => {
    if (!form.name || !form.start_time || !form.end_time) {
      toast.error('Name and shift times are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');

      if (editingShift) {
        await axios.put(
          `${API_BASE_URL}/hr/shifts/${editingShift.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Shift updated');
      } else {
        await axios.post(
          `${API_BASE_URL}/hr/shifts`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Shift created');
      }

      setDialogOpen(false);
      fetchShifts();
    } catch (error) {
      console.error('Error saving shift:', error);
      toast.error(error.response?.data?.detail || 'Failed to save shift');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (shiftId) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/hr/shifts/${shiftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Shift deleted');
      fetchShifts();
    } catch (error) {
      console.error('Error deleting shift:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete shift');
    }
  };

  const formatWorkingDays = (days) => {
    if (!days || days.length === 0) return 'No working days';
    if (days.length === 7) return 'All days';
    return days.map(d => DAYS.find(day => day.value === d)?.label).join(', ');
  };

  const getWeeklyOff = (days) => {
    const offs = DAYS.filter(d => !days?.includes(d.value)).map(d => d.label);
    return offs.length > 0 ? offs.join(', ') : 'None';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-wt-text-primary">Work Shifts</h3>
          <p className="text-sm text-wt-text-muted">Configure different shifts with unique timings and weekly offs</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-wt-gold hover:bg-wt-gold-hover text-black"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Shift
        </Button>
      </div>

      {shifts.length === 0 ? (
        <Card className="bg-wt-bg border-wt-border">
          <CardContent className="py-8 text-center">
            <Clock className="h-12 w-12 mx-auto mb-4 text-wt-text-muted" />
            <p className="text-wt-text-muted">No shifts configured</p>
            <p className="text-sm text-wt-text-muted mt-1">Create your first work shift</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {shifts.map((shift) => (
            <Card key={shift.id} className="bg-wt-bg border-wt-border">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-wt-text-primary">{shift.name}</h4>
                        {shift.is_default && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-wt-gold/20 text-wt-gold text-xs rounded">
                            <Star className="h-3 w-3" />
                            Default
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-wt-text-muted">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {shift.start_time} - {shift.end_time}
                        </span>
                        <span>Grace: {shift.grace_period_minutes}min</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-wt-text-muted">
                        <span>Working: {formatWorkingDays(shift.working_days)}</span>
                        <span className="text-red-400">Weekly Off: {getWeeklyOff(shift.working_days)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenDialog(shift)}
                      className="border-wt-border"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(shift.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              {editingShift ? 'Edit Shift' : 'Add Shift'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label className="text-wt-text-primary">Shift Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Morning Shift, Night Shift"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Start Time *</Label>
                <TimeInput
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">End Time *</Label>
                <TimeInput
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>

            <div>
              <Label className="text-wt-text-primary mb-2 block">Working Days</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDay(day.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      form.working_days.includes(day.value)
                        ? 'bg-wt-gold text-black'
                        : 'bg-wt-bg border border-wt-border text-wt-text-muted hover:border-wt-gold'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-wt-text-muted mt-2">
                Weekly Off: {getWeeklyOff(form.working_days) || 'None'}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-wt-text-primary text-sm">Grace (min)</Label>
                <Input
                  type="number"
                  value={form.grace_period_minutes}
                  onChange={(e) => setForm({ ...form, grace_period_minutes: parseInt(e.target.value) || 0 })}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary text-sm">Late After (min)</Label>
                <Input
                  type="number"
                  value={form.late_threshold_minutes}
                  onChange={(e) => setForm({ ...form, late_threshold_minutes: parseInt(e.target.value) || 0 })}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary text-sm">Half Day (min)</Label>
                <Input
                  type="number"
                  value={form.half_day_threshold_minutes}
                  onChange={(e) => setForm({ ...form, half_day_threshold_minutes: parseInt(e.target.value) || 0 })}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-wt-bg rounded-lg border border-wt-border">
              <div>
                <Label className="text-wt-text-primary">Set as Default Shift</Label>
                <p className="text-xs text-wt-text-muted">New employees will be assigned this shift</p>
              </div>
              <Switch
                checked={form.is_default}
                onCheckedChange={(checked) => setForm({ ...form, is_default: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-wt-border">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editingShift ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftsManagement;
